/**
 * CalendarPage Component
 *
 * Host calendar for managing availability and pricing.
 */

import React, { useState, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { HostLayout } from '../components';
import { useHostListings } from '../hooks';
import {
  CalendarGrid,
  BlockDatesModal,
  PricingModal,
  CalendarLegend,
  ICalSyncPanel,
} from './components';
import { useCalendar, useCalendarActions, CalendarDay } from './hooks';
import { formatDate } from '../../../utils/dates';

export const CalendarPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { listings, isLoading: listingsLoading } = useHostListings();

  // Get listing from URL or default to first listing
  const selectedListingId = searchParams.get('listing') || listings[0]?.id || '';

  // Calendar state
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDates, setSelectedDates] = useState<Date[]>([]);

  // Modal states
  const [showBlockModal, setShowBlockModal] = useState(false);
  const [showPricingModal, setShowPricingModal] = useState(false);

  // Calendar data + actions
  const {
    data: calendarDays,
    isLoading: calendarLoading,
    refetch,
  } = useCalendar(selectedListingId, currentMonth);
  const {
    blockDates,
    unblockDates,
    setCustomPrice,
    clearCustomPrice,
    isUpdating,
  } = useCalendarActions(selectedListingId);

  const selectedListing = useMemo(
    () => listings.find((l) => l.id === selectedListingId),
    [listings, selectedListingId]
  );

  const handleListingChange = (listingId: string) => {
    setSearchParams({ listing: listingId });
    setSelectedDates([]);
  };

  const navigateMonth = (direction: 'prev' | 'next') => {
    setCurrentMonth((prev) => {
      const newMonth = new Date(prev);
      if (direction === 'prev') {
        newMonth.setMonth(newMonth.getMonth() - 1);
      } else {
        newMonth.setMonth(newMonth.getMonth() + 1);
      }
      return newMonth;
    });
  };

  const goToToday = () => {
    setCurrentMonth(new Date());
  };

  const handleDateSelect = (dates: Date[]) => {
    setSelectedDates(dates);
  };

  const handleDayClick = (_date: Date, data?: CalendarDay) => {
    // If clicking on a booking, could open booking details
    if (data?.bookingId) {
      // Could navigate to booking details
      console.log('Booking clicked:', data.bookingId);
    }
  };

  const handleBlock = async (reason?: string) => {
    await blockDates(selectedDates, reason);
    setSelectedDates([]);
    refetch();
  };

  const handleUnblock = async () => {
    await unblockDates(selectedDates);
    setSelectedDates([]);
    refetch();
  };

  const handleSetPrice = async (price: number) => {
    await setCustomPrice(selectedDates, price);
    setSelectedDates([]);
    refetch();
  };

  const handleClearPrice = async () => {
    await clearCustomPrice(selectedDates);
    setSelectedDates([]);
    refetch();
  };

  // Check if any selected dates are blocked (manual blocks can be unblocked)
  const hasBlockedDates = useMemo(() => {
    if (selectedDates.length === 0 || calendarDays.length === 0) return false;
    const selectedKeys = new Set(selectedDates.map((d) => formatDate(d, 'numeric')));
    return calendarDays.some(
      (day) => day.isBlocked && selectedKeys.has(formatDate(day.date, 'numeric'))
    );
  }, [selectedDates, calendarDays]);

  const monthYear = currentMonth.toLocaleDateString('en-US', {
    month: 'long',
    year: 'numeric',
  });

  return (
    <HostLayout title="Calendar">
      {/* Listing Selector */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Select listing
          </label>
          {listingsLoading ? (
            <div className="h-10 w-48 bg-gray-100 rounded-lg animate-pulse" />
          ) : (
            <select
              value={selectedListingId}
              onChange={(e) => handleListingChange(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 min-w-[200px]"
            >
              {listings.map((listing) => (
                <option key={listing.id} value={listing.id}>
                  {listing.title}
                </option>
              ))}
            </select>
          )}
        </div>

        {/* Month Navigation */}
        <div className="flex items-center gap-2">
          <button
            onClick={() => navigateMonth('prev')}
            className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            <ChevronLeftIcon className="w-5 h-5 text-gray-600" />
          </button>
          <div className="px-4 py-2 min-w-[160px] text-center font-medium text-gray-900">
            {monthYear}
          </div>
          <button
            onClick={() => navigateMonth('next')}
            className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            <ChevronRightIcon className="w-5 h-5 text-gray-600" />
          </button>
          <button
            onClick={goToToday}
            className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Today
          </button>
        </div>
      </div>

      {/* Selected Dates Actions */}
      {selectedDates.length > 0 && (
        <div className="mb-6 p-4 bg-primary-50 border border-primary-200 rounded-xl">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <p className="font-medium text-gray-900">
                {selectedDates.length} {selectedDates.length === 1 ? 'night' : 'nights'} selected
              </p>
              <p className="text-sm text-gray-600">
                {selectedDates[0].toLocaleDateString('en-US', {
                  month: 'short',
                  day: 'numeric',
                })}
                {selectedDates.length > 1 &&
                  ` - ${selectedDates[selectedDates.length - 1].toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                  })}`}
              </p>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setShowBlockModal(true)}
                className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 text-sm font-medium"
              >
                <BlockIcon className="w-4 h-4 inline-block mr-1" />
                Block
              </button>
              <button
                onClick={() => setShowPricingModal(true)}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-medium"
              >
                <PriceIcon className="w-4 h-4 inline-block mr-1" />
                Set price
              </button>
              <button
                onClick={() => setSelectedDates([])}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-white text-sm font-medium"
              >
                Clear
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Calendar Legend */}
      <div className="mb-4">
        <CalendarLegend />
      </div>

      {/* Calendar Grid */}
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        {selectedListingId ? (
          <CalendarGrid
            month={currentMonth}
            days={calendarDays}
            isLoading={calendarLoading}
            selectedDates={selectedDates}
            onDateSelect={handleDateSelect}
            onDayClick={handleDayClick}
          />
        ) : (
          <div className="text-center py-12 text-gray-500">
            <CalendarIcon className="w-12 h-12 mx-auto mb-4 text-gray-300" />
            <p>Select a listing to view its calendar</p>
          </div>
        )}
      </div>

      {/* Listing Info */}
      {selectedListing && calendarDays.length > 0 && (
        <div className="mt-6 p-4 bg-gray-50 rounded-xl">
          <h3 className="font-medium text-gray-900 mb-2">Pricing Info</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <p className="text-gray-500">Nightly rate</p>
              <p className="font-semibold">
                ${calendarDays.find((d) => !d.isWeekend)?.basePrice ??
                  selectedListing.pricing.nightlyRate}
              </p>
            </div>
            <div>
              <p className="text-gray-500">Weekend rate</p>
              <p className="font-semibold">
                ${calendarDays.find((d) => d.isWeekend)?.basePrice ??
                  selectedListing.pricing.nightlyRate}
              </p>
            </div>
            <div>
              <p className="text-gray-500">Min nights</p>
              <p className="font-semibold">{calendarDays[0].minNights}</p>
            </div>
            <div>
              <p className="text-gray-500">Custom-priced nights</p>
              <p className="font-semibold">
                {calendarDays.filter((d) => d.customPrice != null).length} this month
              </p>
            </div>
          </div>
        </div>
      )}

      {/* iCal Sync Panel */}
      {selectedListingId && (
        <div className="mt-6">
          <ICalSyncPanel listingId={selectedListingId} />
        </div>
      )}

      {/* Block Dates Modal */}
      <BlockDatesModal
        isOpen={showBlockModal}
        onClose={() => setShowBlockModal(false)}
        selectedDates={selectedDates}
        hasBlockedDates={hasBlockedDates}
        onBlock={handleBlock}
        onUnblock={handleUnblock}
        isLoading={isUpdating}
      />

      {/* Pricing Modal */}
      <PricingModal
        isOpen={showPricingModal}
        onClose={() => setShowPricingModal(false)}
        selectedDates={selectedDates}
        currentPrice={selectedListing?.pricing.nightlyRate || 0}
        onSetPrice={handleSetPrice}
        onClearPrice={handleClearPrice}
        isLoading={isUpdating}
      />
    </HostLayout>
  );
};

// Icons
const ChevronLeftIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
  </svg>
);

const ChevronRightIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
  </svg>
);

const BlockIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
  </svg>
);

const PriceIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const CalendarIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
  </svg>
);

export default CalendarPage;
